import debug from 'debug';
import { AudioController } from '@/controller/audio-controller';
import './i18n/audio';
import '@/audio.css';
// Debug logger
const log = debug('moveo:audio:main');
/**
 * MoveoAudio - Twilio-powered voice widget library
 *
 * This library provides a complete voice communication solution using Twilio's Voice SDK.
 * It creates a floating audio widget that allows users to make voice calls directly from
 * your website.
 *
 * @example
 * // Basic usage - initialize with integration ID
 * const controller = await MoveoAudio.init({
 *   integrationId: 'your-integration-id'
 * });
 *
 * @example
 * // Advanced usage - with custom host
 * const controller = await MoveoAudio.init({
 *   integrationId: 'your-integration-id',
 *   host: 'https://channels.moveo.ai'
 * });
 *
 * // Use the controller to manage calls
 * await controller.startCall();
 * controller.endCall();
 *
 * controller.on('error', (event) => {
 *   console.error('Call error:', event.message);
 * });
 *
 * @example
 * // HTML integration (UMD)
 * <script src="https://cdn.example.com/audio-client.min.js"></script>
 * <script>
 *   window.addEventListener('load', async function() {
 *     const controller = await MoveoAudio.init({
 *       integrationId: 'your-id'
 *     });
 *   });
 * </script>
 *
 * @example
 * // ES Module / TypeScript usage
 * import MoveoAudio, { AudioConfig, AudioController } from './audio-client.min.js';
 *
 * const config: AudioConfig = {
 *   integrationId: 'your-id'
 * };
 *
 * const controller: AudioController = await init(config);
 *
 * @module MoveoAudio
 */
/**
 * Initialize MoveoAudio widget
 *
 * Creates and initializes a Twilio-powered voice widget on your page. The widget appears
 * as a floating button in the bottom-right corner that users can click to start a call.
 *
 * The function automatically:
 * - Fetches access tokens from your backend
 * - Registers the Twilio device
 * - Handles microphone permissions
 * - Manages call state and quality
 *
 * @param config - Audio widget configuration
 * @param config.integrationId - (Required) Your Moveo integration ID
 * @param config.host - Backend URL (default: 'https://channels.moveo.ai')
 *
 * @returns Promise<AudioController> - The initialized audio controller instance with methods to control calls
 *
 * @throws {Error} If integrationId is not provided
 * @throws {Error} If backend token endpoint is not accessible
 * @throws {Error} If Twilio device initialization fails
 *
 * @see {@link AudioConfig} for configuration options
 * @see {@link AudioController} for controller API methods
 * @see {@link https://docs.moveo.ai/audio|Audio Widget Documentation}
 */
export const init = async (config) => {
    log('MoveoAudio.init() called with config', config);
    // Validate configuration
    if (!config.integrationId) {
        const error = new Error('integrationId is required in config');
        log('Initialization failed: integrationId is required', error);
        throw error;
    }
    log('Creating AudioController instance');
    const controller = new AudioController(config);
    log('Initializing AudioController');
    await controller.init();
    log('MoveoAudio initialization complete');
    return controller;
};
/**
 * Audio controller class for managing Twilio voice calls
 *
 * Provides methods to control calls, manage devices, and handle audio state.
 *
 * @typedef {Object} AudioController
 * @property {function(): Promise<void>} startCall - Start a new call
 * @property {function(): void} endCall - End the current call
 * @property {function(): void} destroy - Destroy widget and clean up resources
 * @property {function(string, function): function} on - Subscribe to controller events
 * @property {function(string, function): void} off - Unsubscribe from controller events
 * @property {function(string, function): function} once - Subscribe to the next occurrence
 *
 * @see {@link https://docs.moveo.ai/audio#api-reference|API Reference}
 */
export { AudioController };
/**
 * MoveoAudio - Browser global namespace
 *
 * When consumed via the UMD bundle, the library exposes a namespaced object
 * with an `init` function matching the named export.
 */
export const MoveoAudio = Object.assign(init, {
    init,
    AudioController,
});
// eslint-disable-next-line import/no-default-export
export default MoveoAudio;
